import {
  emptyFilterGroup,
  filterOperatorValidator,
  optionSetOperator,
  supportsOptionSet,
  type FilterOperator,
  type FilterValue,
  type RecordCondition,
  type RecordFilterGroup,
} from "@shared/records/filter-clause";
import { isOneOf } from "@shared/validation/is-one-of";
import { operatorsFor } from "./field-operators";
import type { RecordFieldDef, RecordSort } from "../types";

const CONDITION_SEPARATOR = "~";
const PART_SEPARATOR = ":";
const VALUE_SEPARATOR = "|";

const FILTER_OPERATORS: readonly FilterOperator[] =
  filterOperatorValidator.members.map((member) => member.value);

function encodeValue(value: FilterValue | undefined): string {
  if (value === undefined || value === null) return "";
  if (Array.isArray(value)) {
    return value
      .map((entry) => encodeURIComponent(String(entry)))
      .join(VALUE_SEPARATOR);
  }
  return encodeURIComponent(String(value));
}

function decodeValue(
  raw: string,
  field: RecordFieldDef | undefined,
): FilterValue | undefined {
  if (raw === "") return undefined;
  const parts = raw.split(VALUE_SEPARATOR).map(decodeURIComponent);
  const numeric = field?.kind === "number" || field?.kind === "currency";
  const cast = (entry: string): string | number | boolean => {
    if (numeric) return Number(entry);
    if (field?.kind === "boolean") return entry === "true";
    return entry;
  };
  if (parts.length === 1) return cast(parts[0]);
  return numeric ? parts.map(Number) : parts;
}

export function serializeFilterGroup(group: RecordFilterGroup): string {
  return group.conditions
    .map((condition) => {
      const value = encodeValue(condition.value);
      const head = `${condition.field}${PART_SEPARATOR}${condition.operator}`;
      return value === "" ? head : `${head}${PART_SEPARATOR}${value}`;
    })
    .join(CONDITION_SEPARATOR);
}

function fitToField(
  condition: RecordCondition,
  field: RecordFieldDef | undefined,
): RecordCondition {
  const setOperator = optionSetOperator(condition.operator);
  if (!field || !setOperator || setOperator === condition.operator) {
    return condition;
  }
  const operators = operatorsFor(field);
  if (supportsOptionSet(operators, setOperator)) return condition;
  if (!operators.includes(setOperator)) return condition;
  const [first] = Array.isArray(condition.value)
    ? condition.value.map(String)
    : [condition.value];
  return {
    field: condition.field,
    operator: setOperator,
    value: first ?? undefined,
  };
}

export function parseFilterGroup(
  raw: string | null,
  fields: Map<string, RecordFieldDef>,
  fieldAliases?: Record<string, string>,
): RecordFilterGroup {
  if (!raw) return emptyFilterGroup();
  const conditions: RecordCondition[] = [];
  for (const chunk of raw.split(CONDITION_SEPARATOR)) {
    if (!chunk) continue;
    const [rawField, operator, ...rest] = chunk.split(PART_SEPARATOR);
    if (!rawField || !operator) continue;
    const field = resolveFieldAlias(rawField, fields, fieldAliases);
    if (!field) continue;
    if (!isOneOf(FILTER_OPERATORS, operator)) continue;
    conditions.push(
      fitToField(
        {
          field,
          operator,
          value: decodeValue(rest.join(PART_SEPARATOR), fields.get(field)),
        },
        fields.get(field),
      ),
    );
  }
  return { conjunction: "and", conditions };
}

export function resolveFieldAlias(
  field: string,
  fields: Map<string, RecordFieldDef>,
  fieldAliases: Record<string, string> | undefined,
): string | undefined {
  if (fields.has(field)) return field;
  const aliased = fieldAliases?.[field];
  return aliased && fields.has(aliased) ? aliased : undefined;
}

export function serializeSort(sort: RecordSort | undefined): string | null {
  return sort ? `${sort.field}${PART_SEPARATOR}${sort.direction}` : null;
}

export function parseSort(
  raw: string | null,
  fields: Map<string, RecordFieldDef>,
): RecordSort | undefined {
  if (!raw) return undefined;
  const [field, direction] = raw.split(PART_SEPARATOR);
  if (!fields.get(field)?.sortable) return undefined;
  if (direction !== "asc" && direction !== "desc") return undefined;
  return { field, direction };
}

export function conditionsEqual(
  left: RecordCondition[],
  right: RecordCondition[],
): boolean {
  if (left.length !== right.length) return false;
  const conditionKey = (condition: RecordCondition) =>
    `${condition.field}:${condition.operator}:${encodeValue(condition.value)}`;
  return (
    left.map(conditionKey).sort().join("~") ===
    right.map(conditionKey).sort().join("~")
  );
}
